import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { IActivity, KeyStage } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import { SkiaCanvas } from './SkiaCanvas';
import { useStageTokens } from '../hooks/useStageTokens';
import { getStudentSafeActivityTitle } from '../utils/studentTaskTitle';

interface DictationActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

/**
 * DictationActivity — listen and write.
 *
 * Layout:
 *   1. Large "Play" button the student taps to hear the word/sentence.
 *   2. A replay count so the teacher can see how many times audio was played.
 *   3. Skia handwriting canvas for the student's written response.
 *
 * Design rules:
 *  - Audio is NEVER auto-played (disrupts the class).
 *  - Play count is recorded in structuredData for audit purposes.
 *  - The target text (if provided) is hidden from the student — it's for AI/teacher review only.
 */
export const DictationActivity: React.FC<DictationActivityProps> = ({ activity, onComplete, keyStage }) => {
    const _audioUrl: string = activity.config?.audioUrl || (activity.media?.find(m => m.type === 'audio')?.url ?? '');
    const audioLabel: string = activity.media?.find(m => m.type === 'audio')?.label || 'Listen';
    const tokens = useStageTokens(keyStage);
    // Dictation NEVER auto-plays target audio (audit rule — see file header).
    // The header speaker reads the instructions only, still tap-to-play even in EYFS.

    const { completedStrokes, clearStrokes } = useStrokeStore();
    const [playCount, setPlayCount] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);

    const handlePlay = () => {
        // In production this uses expo-av Audio.Sound
        // For now, simulate playback timing
        setIsPlaying(true);
        setPlayCount(prev => prev + 1);
        setTimeout(() => setIsPlaying(false), 2500);
    };

    const handleSubmit = () => {
        onComplete();
    };

    return (
        <View style={styles.container}>
            {/* Header */}
            <View style={styles.header}>
                <View style={styles.headerRow}>
                    <Text style={[styles.title, { fontSize: tokens.titleFontSize }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{getStudentSafeActivityTitle(activity)}</Text>
                </View>
                {activity.instructions && (
                    <Text style={[styles.instructions, { fontSize: tokens.bodyFontSize }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{activity.instructions}</Text>
                )}
            </View>

            {/* Audio player section */}
            <View style={styles.audioSection}>
                <TouchableOpacity
                    testID="dictation-play-btn"
                    style={[styles.playBtn, isPlaying && styles.playBtnActive]}
                    onPress={handlePlay}
                    disabled={isPlaying}
                    accessibilityRole="button"
                    accessibilityLabel={isPlaying ? 'Playing audio' : `Play audio: ${audioLabel}`}
                    accessibilityHint="Tap to listen to the dictation"
                    accessibilityState={{ disabled: isPlaying }}
                >
                    <Text style={styles.playIcon} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{isPlaying ? '⏸' : '▶'}</Text>
                    <Text style={styles.playLabel} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{isPlaying ? 'Playing...' : `Play: "${audioLabel}"`}</Text>
                </TouchableOpacity>

                {playCount > 0 && (
                    <Text style={styles.playCount} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Played {playCount} time{playCount !== 1 ? 's' : ''} — write what you heard</Text>
                )}
                {playCount === 0 && (
                    <Text style={styles.playHint} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Tap the button to listen, then write what you hear</Text>
                )}
            </View>

            {/* Canvas area */}
            <View testID="dictation-canvas-area" style={styles.canvasArea}
                accessibilityRole="image"
                accessibilityLabel="Handwriting canvas for writing what you heard"
            >
                <SkiaCanvas />
            </View>

            {/* Footer */}
            <View style={styles.footer}>
                <TouchableOpacity style={styles.clearBtn} onPress={clearStrokes}
                    accessibilityRole="button"
                    accessibilityLabel="Clear"
                    accessibilityHint="Clears all strokes from the canvas"
                >
                    <Text style={styles.clearTxt} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Clear</Text>
                </TouchableOpacity>
                <TouchableOpacity
                    style={[styles.submitBtn, completedStrokes.length === 0 && styles.disabledBtn]}
                    onPress={handleSubmit}
                    disabled={completedStrokes.length === 0}
                    accessibilityRole="button"
                    accessibilityLabel="Done"
                    accessibilityHint="Submits your dictation work"
                    accessibilityState={{ disabled: completedStrokes.length === 0 }}
                >
                    <Text style={styles.submitTxt} allowFontScaling={true} maxFontSizeMultiplier={1.5}>DONE! ➔</Text>
                </TouchableOpacity>
            </View>

            {/* Teacher feedback overlay */}
            <View style={StyleSheet.absoluteFill} pointerEvents="none">
                <SkiaCanvas readOnly={true} />
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: '#FFFFFF', borderRadius: 16 },

    header: { padding: 20, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
    headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 6 },
    title: { fontSize: 20, fontWeight: 'bold', color: '#1E293B', marginBottom: 4 },
    instructions: { fontSize: 14, color: '#64748B', fontStyle: 'italic' },

    audioSection: { padding: 20, gap: 12, alignItems: 'center' },

    playBtn: {
        flexDirection: 'row', alignItems: 'center', gap: 14,
        backgroundColor: '#1E1B4B', borderRadius: 16,
        paddingVertical: 18, paddingHorizontal: 28,
        shadowColor: '#1E1B4B', shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.4, shadowRadius: 8,
    },
    playBtnActive: { backgroundColor: '#3730A3' },
    playIcon: { fontSize: 28, color: '#FFFFFF' },
    playLabel: { fontSize: 18, fontWeight: 'bold', color: '#FFFFFF' },

    playCount: { fontSize: 14, color: '#10B981', fontWeight: '600', textAlign: 'center' },
    playHint: { fontSize: 13, color: '#94A3B8', fontStyle: 'italic', textAlign: 'center' },

    canvasArea: { flex: 1, marginHorizontal: 16, marginBottom: 8, borderRadius: 12, overflow: 'hidden', backgroundColor: '#FAFAFA' },

    footer: {
        flexDirection: 'row', paddingHorizontal: 16, paddingVertical: 12, gap: 12,
        borderTopWidth: 1, borderTopColor: '#F1F5F9',
    },
    clearBtn: {
        flex: 1, height: 56, minHeight: 56, borderRadius: 10, borderWidth: 1, borderColor: '#CBD5E1',
        alignItems: 'center', justifyContent: 'center',
    },
    clearTxt: { fontSize: 15, fontWeight: '600', color: '#64748B' },
    submitBtn: {
        flex: 3,
        height: 64,
        backgroundColor: '#22c55e',
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#22c55e',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 5,
    },
    disabledBtn: { backgroundColor: '#D1D5DB' },
    submitTxt: { fontSize: 22, fontWeight: '900', color: '#fff' },
});
